import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  writeBatch,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDocs,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebaseConfig';
import { GlobalCategory, ObjectCategory, LabelSchema } from '../../types/labelSchema';

// ─────────────────────────────────────────────────────────────────────────────
// Multi-photo types and helpers
// ─────────────────────────────────────────────────────────────────────────────
type MediaRole = 'primary' | 'expiration_label' | 'after';

interface SubmissionMediaItem {
  role: MediaRole;
  url: string;
  type?: string;
  storagePath?: string;
}

interface LabelData {
  categoryId: string;
  bbox: [number, number, number, number];
  attributes?: any;
  flagged?: boolean;
  flagReason?: string;
  flagType?: string;
}

interface Submission {
  id: string;
  taskId: string;
  taskTitle: string;
  taskType: string;
  category: string;
  subcategory?: string;
  itemType?: string;
  contentLanguageCode?: string;
  contentLanguageLabel?: string;
  imageUrl?: string;
  mediaUrl?: string;
  labels: any;
  status: string;
  createdAt: any;
  completedAt?: any;
  approvedAt?: any;
  labeledBy?: string;
  approvedBy?: string;
  photoPackageId?: string | null;
  // Multi-photo fields
  media?: SubmissionMediaItem[];
  labelsByRole?: Partial<Record<MediaRole, LabelData[]>>;
  answers?: Record<string, string>;
  // Expiration gating fields
  itemTypeStage?: 'unverified' | 'provisional' | 'final';
  packagingEligibility?: {
    photoPackagesEligible?: boolean;
    datasetPackagesEligible?: boolean;
    eligibleAt?: number;
    eligibleBy?: string;
  };
}

// Check if submission is multi-photo (has media[] with 2+ items)
function isMultiPhotoSubmission(submission: any): boolean {
  return Array.isArray(submission?.media) && (submission?.media?.length ?? 0) >= 2;
}

// Get required roles for a submission (legacy function, now uses stepKeys internally)
// Phase A: Uses stepKeys internally but returns roles for UI compatibility
function getRequiredRoles(submission: any): MediaRole[] {
  // Import helpers dynamically to avoid circular dependencies
  const { getRequiredSteps, stepKeyToRole } = require('../../utils/submissionHelpers');
  
  // Use stepKeys internally (derives from submission.media, NOT task.steps)
  const stepKeys = getRequiredSteps(submission);
  
  // Map stepKeys back to roles for UI display (legacy compatibility)
  const roles: MediaRole[] = [];
  for (const stepKey of stepKeys) {
    const role = stepKeyToRole(stepKey, submission);
    if (role && !roles.includes(role)) {
      roles.push(role);
    }
  }
  
  // Fallback: if no roles found, use stepKey as role (for legacy compatibility)
  if (roles.length === 0) {
    return ['primary'];
  }
  
  return roles;
}

// Get secondary role for multi-photo submission
function getSecondaryRole(submission: any): MediaRole | null {
  if (!isMultiPhotoSubmission(submission)) return null;
  const roles = getRequiredRoles(submission);
  return roles.find(r => r !== 'primary') || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Check if submission is expiration-kind
// ─────────────────────────────────────────────────────────────────────────────
function isExpirationKindSubmission(submission: any, task?: any): boolean {
  const taskKind = task?.taskKind || submission?.task?.taskKind;
  const informationType = task?.informationType || submission?.task?.informationType || submission?.informationType;
  return taskKind === 'expiration' || informationType === 'Expiration Date';
}

// Get image URL for a specific role
function getImageUrlForRole(submission: any, role: MediaRole): string | null {
  if (!submission) return null;
  
  if (role === 'primary') {
    // Prefer imageUrl/mediaUrl for backward compatibility
    if (submission.imageUrl) return submission.imageUrl;
    if (submission.mediaUrl) return submission.mediaUrl;
    // Fallback to media array
    if (Array.isArray(submission.media)) {
      const primaryMedia = submission.media.find((m: any) => m.role === 'primary');
      if (primaryMedia?.url) return primaryMedia.url;
    }
    return null;
  }
  
  // Secondary role
  if (Array.isArray(submission.media)) {
    const roleMedia = submission.media.find((m: any) => m.role === role);
    return roleMedia?.url || null;
  }
  return null;
}

// Get labels for active role (using stepKeys internally)
function getActiveLabels(submission: any, activeRole: MediaRole): LabelData[] {
  if (!submission) return [];
  
  // Import helpers dynamically to avoid circular dependencies
  const { getLabelsForStep, stepKeyFromMedia } = require('../../utils/submissionHelpers');
  
  // Find corresponding stepKey for this role
  const mediaItem = submission.media?.find((m: any) => m.role === activeRole);
  const stepKey = stepKeyFromMedia(mediaItem);
  
  // Use helper to read labels with fallback priority
  return getLabelsForStep(submission, stepKey);
}

// Get display label for a role
function getRoleDisplayLabel(role: MediaRole, submission: any): string {
  if (role === 'primary') return 'Primary';
  if (role === 'expiration_label') return 'Expiration Label';
  if (role === 'after') return 'After';
  return role;
}

interface Task {
  id: string;
  title: string;
  category: string;
  subcategory?: string;
  itemType?: string;
  active?: boolean;
}

interface PhotoPackage {
  id: string;
  name: string;
  description?: string;
  createdAt: any;
  createdBy: string;
  priceUsd?: number;
  tags?: string[];
  version?: number | string;
  isPublished: boolean;
  stats?: {
    images: number;
    categories: string[];
  };
  bundleUri?: string;
  languages?: string[];
  languageLabels?: string[];
  primaryLanguageCode?: string;
  languageMix?: 'single' | 'mixed';
}

const defaultFilters = {
  task: 'all',
  category: 'all',
  subcategory: 'all',
  itemType: 'all',
  language: 'all',
  taskType: 'Photo' as string,
  dateRange: 'all',
  photoPackageAssignment: 'unassigned' as 'all' | 'unassigned',
  marketplaceStatus: 'all' as 'all' | 'in' | 'not',
};

const defaultPackageForm = {
  name: '',
  packageId: '',
  description: '',
  priceUsd: '',
  tags: '',
  version: '1',
  publishNow: false,
};

export default function PhotoPackagesPage() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [tasks, setTasks] = useState<Record<string, Task>>({});
  const [packages, setPackages] = useState<PhotoPackage[]>([]);
  const [globalCategories, setGlobalCategories] = useState<Record<string, GlobalCategory>>({});
  const [objectCategories, setObjectCategories] = useState<Record<string, ObjectCategory>>({});
  const [labelSchemas, setLabelSchemas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(defaultFilters);
  const [activeTab, setActiveTab] = useState<'submissions' | 'packages'>('submissions');
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState<Set<string>>(new Set());
  const [selectedPackageIds, setSelectedPackageIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingSubmissions, setIsExportingSubmissions] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [packageForm, setPackageForm] = useState(defaultPackageForm);
  const [creatingPackage, setCreatingPackage] = useState(false);
  const [editingPackage, setEditingPackage] = useState<PhotoPackage | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [activeRoleDetails, setActiveRoleDetails] = useState<MediaRole>('primary');
  const auth = getAuth();

  useEffect(() => {
    document.title = 'Raw Photos — Pixano';
  }, []);

  useEffect(() => {
    // Load global categories, object categories, and label schemas
    const loadCategories = async () => {
      try {
        // Load global categories
        const globalSnapshot = await getDocs(collection(db, 'globalCategories'));
        const globalMap: Record<string, GlobalCategory> = {};
        globalSnapshot.docs.forEach((docSnap) => {
          const category = { id: docSnap.id, ...docSnap.data() } as GlobalCategory;
          globalMap[docSnap.id] = category;
        });
        setGlobalCategories(globalMap);

        // Load object categories
        const objectSnapshot = await getDocs(collection(db, 'objectCategories'));
        const objectMap: Record<string, ObjectCategory> = {};
        objectSnapshot.docs.forEach((docSnap) => {
          const category = { id: docSnap.id, ...docSnap.data() } as ObjectCategory;
          objectMap[docSnap.id] = category;
        });
        setObjectCategories(objectMap);

        // Load label schemas for schema class lookups
        const schemasSnapshot = await getDocs(collection(db, 'labelSchemas'));
        const schemasData = schemasSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })) as any[];
        setLabelSchemas(schemasData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();

    let currentTasks: Record<string, Task> = {};

    const unsubscribeTasks = onSnapshot(
      collection(db, 'tasks'),
      (snapshot) => {
        const tasksData: Record<string, Task> = {};
        snapshot.docs.forEach((docSnap) => {
          tasksData[docSnap.id] = { id: docSnap.id, ...docSnap.data() } as Task;
        });
        currentTasks = tasksData; // Update closure variable
        setTasks(tasksData);
        setTasksLoading(false);
        console.log('Tasks loaded:', Object.keys(tasksData).length, 'tasks');
      },
      (error) => {
        console.error('Error loading tasks:', error);
        currentTasks = {};
        setTasks({});
        setTasksLoading(false);
      }
    );

    // Query photo-ready submissions: taskType === 'Photo' AND (status === 'approved' OR status === 'completed')
    // This allows newly approved photos to appear immediately, plus older completed ones
    // Note: We query by taskType and filter status in memory to avoid composite index requirements
    const submissionsQuery = query(
      collection(db, 'submissions'),
      where('taskType', '==', 'Photo')
    );
    const unsubscribeSubmissions = onSnapshot(
      submissionsQuery,
      (snapshot) => {
        const allSubmissions = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })) as Submission[];
        
        // Filter to only approved or completed status (photo-ready)
        // IMPORTANT: Also include 'needs_relabeling' for ALL Photo submissions (not just those with photoPackageId)
        // This allows:
        // 1. Submissions to remain in Photo Packages even when sent back to workbench from Data Packages
        // 2. Photo submissions that lost their photoPackageId to still be visible
        // The same submission can exist in both Photo Packages and Data Packages independently
        // EXPIRATION GATING: Expiration-kind submissions require packagingEligibility.photoPackagesEligible === true AND itemTypeStage === 'final'
        const submissionsData = allSubmissions.filter(
          (sub) => {
            const isPhotoReady = sub.status === 'approved' || sub.status === 'completed' || sub.status === 'labeled';
            // Include needs_relabeling for ALL Photo submissions (they may have lost photoPackageId)
            const isPhotoNeedsRelabeling = sub.status === 'needs_relabeling';
            
            // Get task for expiration-kind detection (use closure variable to get latest tasks)
            const task = currentTasks[sub.taskId];
            const isExpiration = isExpirationKindSubmission(sub, task);
            
            // For expiration-kind submissions, require packaging eligibility
            if (isExpiration) {
              const packagingEligibility = sub.packagingEligibility || {};
              const itemTypeStage = sub.itemTypeStage;
              // Must have photoPackagesEligible === true AND itemTypeStage === 'final'
              if (!packagingEligibility.photoPackagesEligible || itemTypeStage !== 'final') {
                return false;
              }
            }
            
            return isPhotoReady || isPhotoNeedsRelabeling;
          }
        );
        
        submissionsData.sort((a, b) => {
          // Sort by completedAt if available, otherwise approvedAt, otherwise createdAt
          const aTime = a.completedAt?.toMillis?.() || a.approvedAt?.toMillis?.() || a.completedAt || a.approvedAt || a.createdAt?.toMillis?.() || a.createdAt || 0;
          const bTime = b.completedAt?.toMillis?.() || b.approvedAt?.toMillis?.() || b.completedAt || b.approvedAt || b.createdAt?.toMillis?.() || b.createdAt || 0;
          return bTime - aTime;
        });
        setSubmissions(submissionsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading submissions:', error);
        setSubmissions([]);
        setLoading(false);
      }
    );

    const unsubscribePackages = onSnapshot(
      collection(db, 'photoPackages'),
      (snapshot) => {
        const packagesData = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })) as PhotoPackage[];
        packagesData.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.()?.getTime() || 0;
          const bTime = b.createdAt?.toDate?.()?.getTime() || 0;
          return bTime - aTime;
        });
        setPackages(packagesData);
      },
      (error) => {
        console.error('Error loading photo packages:', error);
        setPackages([]);
      }
    );

    return () => {
      unsubscribeTasks();
      unsubscribeSubmissions();
      unsubscribePackages();
    };
  }, []);

  useEffect(() => {
    // Reset selection whenever filters change
    setSelectedSubmissionIds(new Set());
  }, [filters]);

  useEffect(() => {
    // Reset package selection when switching tabs
    if (activeTab === 'submissions') {
      setSelectedPackageIds(new Set());
    }
  }, [activeTab]);

  const getLabelsCount = (labels: any) => {
    if (!labels) return 0;
    if (Array.isArray(labels)) return labels.length;
    if (typeof labels === 'object') return Object.keys(labels).length;
    return 0;
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Get object info from categoryId - matches DatasetDetail.tsx logic
  const getObjectInfo = (categoryId: string) => {
    if (!categoryId) return null;

    // Build schema class map from labelSchemas
    const schemaClassMap = new Map<string, any>();
    labelSchemas.forEach((schema: any) => {
      const classes = schema.classes || [];
      classes.forEach((cls: any) => {
        if (cls.id) {
          schemaClassMap.set(cls.id, {
            displayName: cls.displayName || cls.name,
            objectId: cls.id,
            objectName: cls.name,
            description: cls.description,
            category: (cls as any).category, // NEW: top-level category
            subcategory: (cls as any).subcategory || (cls as any).category, // RENAMED: subcategory (fallback for migration)
            shapes: cls.shapes,
            source: 'schema'
          });
        }
      });
    });

    // Priority 1: Check schema classes first
    if (schemaClassMap.has(categoryId)) {
      const schemaObj = schemaClassMap.get(categoryId)!;
      // Use subcategory (renamed from category) with fallback for migration
      const subcategoryId = schemaObj.subcategory || schemaObj.category;
      const categoryDisplayName = subcategoryId && objectCategories[subcategoryId]
        ? (objectCategories[subcategoryId].displayName || objectCategories[subcategoryId].name)
        : undefined;
      return {
        displayName: schemaObj.displayName,
        objectId: schemaObj.objectId,
        objectName: schemaObj.objectName,
        description: schemaObj.description,
        category: schemaObj.category, // NEW: top-level category
        subcategory: subcategoryId, // RENAMED: subcategory
        categoryDisplayName,
        shapes: schemaObj.shapes,
        source: 'schema'
      };
    }

    // Priority 2: Check global categories
    if (globalCategories[categoryId]) {
      const globalObj = globalCategories[categoryId];
      // Use subcategory (renamed from category) with fallback for migration
      const subcategoryId = globalObj.subcategory || globalObj.category;
      const categoryDisplayName = subcategoryId && objectCategories[subcategoryId]
        ? (objectCategories[subcategoryId].displayName || objectCategories[subcategoryId].name)
        : undefined;
      return {
        displayName: globalObj.displayName,
        objectId: globalObj.id,
        objectName: globalObj.name,
        description: globalObj.description,
        category: globalObj.category, // NEW: top-level category
        subcategory: subcategoryId, // RENAMED: subcategory
        categoryDisplayName,
        shapes: globalObj.shapes,
        color: globalObj.color,
        source: 'global'
      };
    }

    return null;
  };

  const parseLabels = (labels: any) => {
    if (!labels) return [];
    if (Array.isArray(labels)) {
      return labels.map((label: any, index: number) => {
        // ONLY use categoryId - this is the document ID in globalCategories or schema class ID
        // Do NOT fall back to classId, class, className - those are not valid IDs
        const categoryId = label.categoryId;
        if (!categoryId) {
          return {
            objectName: `Label ${index + 1} (No categoryId)`,
            objectId: 'unknown',
            objectDescription: 'Label missing categoryId',
            bboxInfo: '',
            attributes: label.attributes || {},
            raw: label
          };
        }

        const objectInfo = getObjectInfo(categoryId);
        
        if (!objectInfo) {
          // Category ID not found - show the raw ID
          return {
            objectName: `Unknown (${categoryId})`,
            objectId: categoryId,
            objectDescription: `Category ID not found in globalCategories or labelSchemas`,
            bboxInfo: '',
            attributes: label.attributes || {},
            raw: label
          };
        }

        let bboxInfo = '';
        if (Array.isArray(label.bbox) && label.bbox.length === 4) {
          const [x1, y1, x2, y2] = label.bbox;
          bboxInfo = `BBox: [${x1}, ${y1}, ${x2}, ${y2}]`;
        } else if (label.x !== undefined && label.y !== undefined && label.width !== undefined && label.height !== undefined) {
          bboxInfo = `Position: (${label.x}, ${label.y}), Size: ${label.width}×${label.height}`;
        }
        
        return {
          objectName: objectInfo.displayName,
          objectId: objectInfo.objectId,
          objectDescription: objectInfo.description || 'No description available',
          bboxInfo,
          attributes: label.attributes || {},
          raw: label
        };
      });
    }
    if (typeof labels === 'object') {
      return Object.entries(labels).map(([key, value]: [string, any]) => {
        const objectInfo = getObjectInfo(key);
        if (!objectInfo) {
          return {
            objectName: `Unknown (${key})`,
            objectId: key,
            objectDescription: `Category ID not found`,
            bboxInfo: typeof value === 'object' ? JSON.stringify(value) : String(value),
            attributes: {},
            raw: value
          };
        }
        return {
          objectName: objectInfo.displayName,
          objectId: objectInfo.objectId,
          objectDescription: objectInfo.description || 'No description available',
          bboxInfo: typeof value === 'object' ? JSON.stringify(value) : String(value),
          attributes: {},
          raw: value
        };
      });
    }
    return [];
  };

  // Filter submissions - only show Photo task type
  const filteredSubmissions = submissions.filter((submission) => {
    // Always filter by Photo task type
    if (submission.taskType !== 'Photo') return false;

    const task = tasks[submission.taskId];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const matchesSearch =
        submission.id.toLowerCase().includes(lower) ||
        submission.taskTitle?.toLowerCase().includes(lower) ||
        submission.category?.toLowerCase().includes(lower) ||
        task?.title?.toLowerCase().includes(lower);
      if (!matchesSearch) return false;
    }

    if (filters.task !== 'all' && submission.taskId !== filters.task) return false;
    if (filters.category !== 'all' && submission.category !== filters.category) return false;
    if (filters.subcategory !== 'all' && submission.subcategory !== filters.subcategory) return false;
    if (filters.itemType !== 'all' && (submission.itemType || task?.itemType) !== filters.itemType) return false;
    if (filters.language !== 'all' && submission.contentLanguageCode !== filters.language) return false;

    if (filters.photoPackageAssignment === 'unassigned' && submission.photoPackageId) return false;

    if (filters.marketplaceStatus === 'in' && !(submission as any).marketplace?.rawPhoto) return false;
    if (filters.marketplaceStatus === 'not' && (submission as any).marketplace?.rawPhoto) return false;

    if (filters.dateRange !== 'all') {
      const now = Date.now();
      // Use completedAt if available, otherwise approvedAt, otherwise createdAt
      const approvalTime = submission.completedAt?.toMillis?.() || 
                          submission.approvedAt?.toMillis?.() || 
                          submission.completedAt || 
                          submission.approvedAt || 
                          submission.createdAt?.toMillis?.() || 
                          submission.createdAt || 
                          0;
      const daysDiff = (now - approvalTime) / (1000 * 60 * 60 * 24);
      if (filters.dateRange === 'today' && daysDiff > 1) return false;
      if (filters.dateRange === 'week' && daysDiff > 7) return false;
      if (filters.dateRange === 'month' && daysDiff > 30) return false;
    }

    return true;
  });

  // Only include Photo submissions in stats
  const photoSubmissions = submissions.filter((s) => s.taskType === 'Photo');
  const totalLabels = photoSubmissions.reduce((sum, s) => sum + getLabelsCount(s.labels), 0);
  const filteredLabels = filteredSubmissions.reduce((sum, s) => sum + getLabelsCount(s.labels), 0);
  const uniqueCategories = [...new Set(photoSubmissions.map((s) => s.category).filter(Boolean))];
  const uniqueSubcategories =
    filters.category === 'all'
      ? [...new Set(photoSubmissions.map((s) => s.subcategory).filter(Boolean))]
      : [
          ...new Set(
            photoSubmissions
              .filter((s) => s.category === filters.category && s.subcategory)
              .map((s) => s.subcategory)
              .filter(Boolean)
          ),
        ];
  const uniqueItemTypes = [
    ...new Set(
      photoSubmissions
        .filter((s) => filters.category === 'all' || s.category === filters.category)
        .filter((s) => filters.subcategory === 'all' || s.subcategory === filters.subcategory)
        .map((s) => s.itemType)
        .filter((type): type is string => Boolean(type))
    ),
  ].sort((a, b) => a.localeCompare(b));
  const allTasks = Object.values(tasks);
  const activeTasks = allTasks.filter((task) => task.active === true);
  const inactiveTasks = allTasks.filter((task) => task.active === false);
  const uniqueTasks = [...activeTasks, ...inactiveTasks]
    .filter((task) => {
      // Only show Photo tasks
      const taskData = tasks[task.id];
      return taskData && photoSubmissions.some((s) => s.taskId === task.id);
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  const selectedSubmissions = useMemo(() => {
    return filteredSubmissions.filter((submission) => selectedSubmissionIds.has(submission.id));
  }, [filteredSubmissions, selectedSubmissionIds]);

  const slugify = (value: string) =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  useEffect(() => {
    if (!showCreateModal) return;
    if (!packageForm.name) return;
    const slug = slugify(packageForm.name);
    setPackageForm((prev) => ({
      ...prev,
      packageId: slug,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageForm.name, showCreateModal]);

  const handleSelectSubmission = (submissionId: string, checked: boolean) => {
    setSelectedSubmissionIds((prev) => {
      const updated = new Set(prev);
      if (checked) {
        updated.add(submissionId);
      } else {
        updated.delete(submissionId);
      }
      return updated;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSubmissionIds(new Set(filteredSubmissions.map((s) => s.id)));
    } else {
      setSelectedSubmissionIds(new Set());
    }
  };

  const handleReleaseToMarketplace = async () => {
    if (selectedSubmissions.length === 0) {
      alert('Select at least one submission to release.');
      return;
    }
    const count = selectedSubmissions.length;
    const confirmed = window.confirm(
      `Release ${count} photo${count === 1 ? '' : 's'} to the Raw Photos marketplace? Customers will be able to purchase ${count === 1 ? 'it' : 'them'} with credits.`
    );
    if (!confirmed) return;

    setCreatingPackage(true);
    try {
      const ids = selectedSubmissions.map((s) => s.id);
      // Firestore batches are capped at 500 writes; chunk to stay safely under that.
      const CHUNK_SIZE = 400;
      for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        const chunk = ids.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        chunk.forEach((id) => {
          const submissionRef = doc(db, 'submissions', id);
          // Dot notation updates ONLY marketplace.rawPhoto and preserves any
          // other keys under marketplace (e.g. annotatedPhoto added later).
          batch.update(submissionRef, {
            'marketplace.rawPhoto': true,
            'marketplace.rawPhotoReleasedAt': serverTimestamp(),
          });
        });
        await batch.commit();
      }
      setSelectedSubmissionIds(new Set());
      alert(`Released ${count} photo${count === 1 ? '' : 's'} to the marketplace.`);
    } catch (error) {
      console.error('Error releasing to marketplace:', error);
      alert('Failed to release photos to the marketplace.');
    } finally {
      setCreatingPackage(false);
    }
  };

  const handleRecallFromMarketplace = async () => {
    if (selectedSubmissions.length === 0) {
      alert('Select at least one submission to recall.');
      return;
    }
    const count = selectedSubmissions.length;
    const confirmed = window.confirm(
      `Recall ${count} item${count === 1 ? '' : 's'} from the marketplace? They will no longer be available for purchase. Customers who already bought them keep their copies.`
    );
    if (!confirmed) return;

    setCreatingPackage(true);
    try {
      const ids = selectedSubmissions.map((s) => s.id);
      const CHUNK_SIZE = 400;
      for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        const chunk = ids.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        chunk.forEach((id) => {
          // ONLY flips marketplace.rawPhoto — never touches ownership / clients/*.
          batch.update(doc(db, 'submissions', id), {
            'marketplace.rawPhoto': false,
            'marketplace.rawPhotoRecalledAt': serverTimestamp(),
          });
        });
        await batch.commit();
      }
      setSelectedSubmissionIds(new Set());
      alert(`Recalled ${count} photo${count === 1 ? '' : 's'} from the marketplace.`);
    } catch (error) {
      console.error('Error recalling from marketplace:', error);
      alert('Failed to recall photos from the marketplace.');
    } finally {
      setCreatingPackage(false);
    }
  };

  const handleCreatePackage = async () => {
    if (!packageForm.name || !packageForm.packageId) {
      alert('Please provide both a name and package ID.');
      return;
    }
    if (selectedSubmissions.length === 0) {
      alert('Select at least one submission.');
      return;
    }

    const packageId = packageForm.packageId;
    const packageRef = doc(db, 'photoPackages', packageId);
    const creatorId = auth.currentUser?.uid || 'unknown';
    const tags = packageForm.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    const categories = Array.from(new Set(selectedSubmissions.map((s) => s.category).filter(Boolean)));

    const LANGUAGE_LABELS: Record<string, string> = {
      en: 'English',
      vi: 'Vietnamese',
      es: 'Spanish',
      pt: 'Portuguese',
      th: 'Thai',
      hi: 'Hindi',
      de: 'German',
      fr: 'French',
      it: 'Italian',
      id: 'Indonesian',
      tl: 'Filipino',
    };
    const allLanguageCodes = selectedSubmissions
      .map((s: any) => s.contentLanguageCode)
      .filter((c: any) => typeof c === 'string' && c.length > 0);
    const uniqueLanguageCodes = [...new Set(allLanguageCodes)] as string[];
    const uniqueLanguageLabels = uniqueLanguageCodes.map(
      (c) => LANGUAGE_LABELS[c] ?? c
    );
    const primaryLanguageCode =
      uniqueLanguageCodes.length === 1 ? uniqueLanguageCodes[0] : '';
    const languageMix: 'single' | 'mixed' =
      uniqueLanguageCodes.length > 1 ? 'mixed' : 'single';

    const packageData = {
      name: packageForm.name,
      description: packageForm.description || '',
      createdAt: serverTimestamp(),
      createdBy: creatorId,
      priceUsd: packageForm.priceUsd ? Number(packageForm.priceUsd) : undefined,
      tags,
      version: packageForm.version || '1',
      isPublished: packageForm.publishNow,
      stats: {
        images: selectedSubmissions.length,
        categories,
      },
      languages: uniqueLanguageCodes,
      languageLabels: uniqueLanguageLabels,
      primaryLanguageCode,
      languageMix,
    };

    setCreatingPackage(true);

    try {
      await setDoc(packageRef, packageData);

      const batch = writeBatch(db);
      selectedSubmissions.forEach((submission) => {
        const submissionRef = doc(db, 'submissions', submission.id);
        batch.update(submissionRef, { photoPackageId: packageId });
      });
      await batch.commit();

      setShowCreateModal(false);
      setPackageForm(defaultPackageForm);
      setSelectedSubmissionIds(new Set());
      alert('Photo package created successfully!');
    } catch (error) {
      console.error('Error creating photo package:', error);
      alert('Failed to create photo package.');
    } finally {
      setCreatingPackage(false);
    }
  };

  const handleEditPackage = (pkg: PhotoPackage) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name,
      packageId: pkg.id,
      description: pkg.description || '',
      priceUsd: pkg.priceUsd?.toString() || '',
      tags: (pkg.tags || []).join(', '),
      version: pkg.version?.toString() || '1',
      publishNow: pkg.isPublished,
    });
    setShowEditModal(true);
  };

  const handleUpdatePackage = async () => {
    if (!editingPackage) return;
    const pkgRef = doc(db, 'photoPackages', editingPackage.id);
    const tags = packageForm.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    try {
      await updateDoc(pkgRef, {
        name: packageForm.name,
        description: packageForm.description,
        priceUsd: packageForm.priceUsd ? Number(packageForm.priceUsd) : null,
        tags,
        version: packageForm.version,
        isPublished: packageForm.publishNow,
        updatedAt: serverTimestamp(),
      });
      setShowEditModal(false);
      setEditingPackage(null);
      alert('Photo package updated.');
    } catch (error) {
      console.error('Error updating photo package:', error);
      alert('Failed to update photo package.');
    }
  };

  const togglePublishPackage = async (pkg: PhotoPackage) => {
    const pkgRef = doc(db, 'photoPackages', pkg.id);
    try {
      await updateDoc(pkgRef, {
        isPublished: !pkg.isPublished,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error toggling publish state:', error);
      alert('Failed to update publish status.');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Raw Photos</h1>
            <p className="text-gray-600 mt-1">Raw photos available to release for client purchase in the Marketplace.</p>
          </div>
        </div>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading submissions...</p>
        </div>
      </div>
    );
  }

  const handlePackageSelect = (packageId: string, checked: boolean) => {
    setSelectedPackageIds((prev) => {
      const updated = new Set(prev);
      if (checked) {
        updated.add(packageId);
      } else {
        updated.delete(packageId);
      }
      return updated;
    });
  };

  const handleSelectAllPackages = (checked: boolean) => {
    if (checked) {
      setSelectedPackageIds(new Set(packages.map((pkg) => pkg.id)));
    } else {
      setSelectedPackageIds(new Set());
    }
  };

  // Helper to download JSON blob
  const downloadJson = (payload: any, filename: string) => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Client-side fallback export for submissions
  const exportSubmissionsClientSide = (subsToExport: Submission[], filename: string) => {
    const payload = {
      exportedAt: new Date().toISOString(),
      type: 'photo',
      count: subsToExport.length,
      submissions: subsToExport.map((s: any) => ({
        id: s.id,
        taskId: s.taskId,
        taskTitle: s.taskTitle,
        taskType: s.taskType,
        category: s.category,
        subcategory: s.subcategory,
        itemType: s.itemType,
        status: s.status,
        createdAt: s.createdAt?.toDate?.()?.toISOString() || s.createdAt,
        approvedAt: s.approvedAt?.toDate?.()?.toISOString() || s.approvedAt,
        completedAt: s.completedAt?.toDate?.()?.toISOString() || s.completedAt,
        mediaUrl: s.mediaUrl,
        imageUrl: s.imageUrl,
        // Multi-photo fields
        media: s.media || undefined,
        labels: s.labels,
        labelsByRole: s.labelsByRole || undefined,
        answers: s.answers || undefined,
        photoPackageId: s.photoPackageId,
        labeledBy: s.labeledBy,
        approvedBy: s.approvedBy,
        contentLanguageCode: s.contentLanguageCode || '',
        contentLanguageLabel: s.contentLanguageLabel || '',
      }))
    };
    downloadJson(payload, filename);
  };

  const handleExport = async () => {
    if (selectedPackageIds.size === 0) {
      alert('Please select at least one package to export.');
      return;
    }

    setIsExporting(true);
    try {
      // Try Cloud Function first
      const { httpsCallable } = await import('firebase/functions');
      const { getFunctions } = await import('firebase/functions');
      const functions = getFunctions();
      const adminExportPackages = httpsCallable(functions, 'adminExportPackages');

      const result = await adminExportPackages({
        type: 'photo',
        ids: Array.from(selectedPackageIds),
        previewOnly: true,
      });

      const data = result.data as any;
      const payload = data.json ?? data;
      downloadJson(payload, `pixano-admin-export-photos-${new Date().toISOString().split('T')[0]}.json`);
    } catch (error: any) {
      console.warn('Cloud Function export failed, using client-side fallback:', error?.code || error?.message);
      
      // Client-side fallback: export submissions belonging to selected packages
      const packageSubmissions = submissions.filter(s => 
        s.photoPackageId && selectedPackageIds.has(s.photoPackageId)
      );
      
      if (packageSubmissions.length === 0) {
        alert('No submissions found in selected packages.');
        setIsExporting(false);
        return;
      }
      
      const payload = {
        exportedAt: new Date().toISOString(),
        type: 'photo',
        packages: Array.from(selectedPackageIds).map(pkgId => {
          const pkg = packages.find(p => p.id === pkgId);
          return {
            id: pkgId,
            name: pkg?.name,
            description: pkg?.description,
            stats: pkg?.stats,
            languages: pkg?.languages || [],
            languageLabels: pkg?.languageLabels || [],
            primaryLanguageCode: pkg?.primaryLanguageCode || '',
            languageMix: pkg?.languageMix || 'single',
          };
        }),
        count: packageSubmissions.length,
        submissions: packageSubmissions.map((s: any) => ({
          id: s.id,
          taskId: s.taskId,
          taskTitle: s.taskTitle,
          taskType: s.taskType,
          category: s.category,
          subcategory: s.subcategory,
          itemType: s.itemType,
          status: s.status,
          createdAt: s.createdAt?.toDate?.()?.toISOString() || s.createdAt,
          approvedAt: s.approvedAt?.toDate?.()?.toISOString() || s.approvedAt,
          completedAt: s.completedAt?.toDate?.()?.toISOString() || s.completedAt,
          mediaUrl: s.mediaUrl,
          imageUrl: s.imageUrl,
          media: s.media || undefined,
          labels: s.labels,
          labelsByRole: s.labelsByRole || undefined,
          answers: s.answers || undefined,
          photoPackageId: s.photoPackageId,
          labeledBy: s.labeledBy,
          approvedBy: s.approvedBy,
          contentLanguageCode: s.contentLanguageCode || '',
          contentLanguageLabel: s.contentLanguageLabel || '',
        }))
      };
      downloadJson(payload, `pixano-admin-export-photos-${new Date().toISOString().split('T')[0]}.json`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportSubmissions = async () => {
    if (selectedSubmissionIds.size === 0) {
      alert("Please select at least one submission to export.");
      return;
    }

    setIsExportingSubmissions(true);
    try {
      // Try Cloud Function first
      const { httpsCallable } = await import('firebase/functions');
      const { getFunctions } = await import('firebase/functions');
      const functions = getFunctions();
      const adminExportSubmissions = httpsCallable(functions, "adminExportSubmissions");

      const result = await adminExportSubmissions({
        type: "photo",
        submissionIds: Array.from(selectedSubmissionIds),
      });

      const data = result.data as any;
      const payload = data.json ?? data;
      downloadJson(payload, `pixano-admin-export-photo-submissions-${new Date().toISOString().split('T')[0]}.json`);
    } catch (error: any) {
      console.warn("Cloud Function export failed, using client-side fallback:", error?.code || error?.message);
      
      // Client-side fallback
      const subsToExport = submissions.filter(s => selectedSubmissionIds.has(s.id));
      exportSubmissionsClientSide(subsToExport, `pixano-admin-export-photo-submissions-${new Date().toISOString().split('T')[0]}.json`);
    } finally {
      setIsExportingSubmissions(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Raw Photos</h1>
          <p className="text-gray-600 mt-1">Raw photos available to release for client purchase in the Marketplace.</p>
        </div>
        {activeTab === 'packages' && (
          <div className="flex items-center gap-3">
            {selectedPackageIds.size > 0 && (
              <span className="text-sm text-gray-600">
                Selected: {selectedPackageIds.size}
              </span>
            )}
            <button
              onClick={handleExport}
              disabled={selectedPackageIds.size === 0 || isExporting}
              className={`px-4 py-2 rounded-lg font-medium ${
                selectedPackageIds.size === 0 || isExporting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isExporting ? 'Exporting...' : `Export${selectedPackageIds.size > 0 ? ` (${selectedPackageIds.size} selected)` : ''}`}
            </button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('submissions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'submissions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Submissions
          </button>
          <button
            onClick={() => setActiveTab('packages')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'packages'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Packages
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'submissions' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-blue-600">{photoSubmissions.filter((s) => !s.photoPackageId).length}</div>
              <div className="text-sm text-gray-600">Total Unassigned</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-purple-600">{uniqueCategories.length}</div>
              <div className="text-sm text-gray-600">Total Categories</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-orange-600">{uniqueSubcategories.length}</div>
              <div className="text-sm text-gray-600">Total SubCategories</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-green-600">{uniqueItemTypes.length}</div>
              <div className="text-sm text-gray-600">Total Items</div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="flex items-center gap-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by ID, title, category..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilters(defaultFilters);
                }}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm whitespace-nowrap"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, category: e.target.value, subcategory: 'all', itemType: 'all' }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Categories</option>
                  {uniqueCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                <select
                  value={filters.subcategory}
                  onChange={(e) => setFilters((prev) => ({ ...prev, subcategory: e.target.value, itemType: 'all' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={filters.category === 'all'}
                >
                  <option value="all">All Subcategories</option>
                  {uniqueSubcategories.map((subcategory) => (
                    <option key={subcategory} value={subcategory}>
                      {subcategory}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
                <select
                  value={filters.itemType}
                  onChange={(e) => setFilters((prev) => ({ ...prev, itemType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={filters.category === 'all' && filters.subcategory === 'all'}
                >
                  <option value="all">All Item Types</option>
                  {uniqueItemTypes.map((itemType) => (
                    <option key={itemType} value={itemType}>
                      {itemType}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                <select
                  value={filters.language}
                  onChange={(e) => setFilters((prev) => ({ ...prev, language: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Languages</option>
                  <option value="en">English</option>
                  <option value="vi">Vietnamese</option>
                  <option value="es">Spanish</option>
                  <option value="pt">Portuguese</option>
                  <option value="th">Thai</option>
                  <option value="hi">Hindi</option>
                  <option value="de">German</option>
                  <option value="fr">French</option>
                  <option value="it">Italian</option>
                  <option value="id">Indonesian</option>
                  <option value="tl">Filipino</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
                <select
                  value={filters.taskType}
                  onChange={(e) => setFilters((prev) => ({ ...prev, taskType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                  disabled
                >
                  <option value="Photo">Photo</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task</label>
                <select
                  value={filters.task}
                  onChange={(e) => setFilters((prev) => ({ ...prev, task: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Tasks</option>
                  {tasksLoading ? (
                    <option value="" disabled>Loading tasks...</option>
                  ) : uniqueTasks.length === 0 ? (
                    <option value="" disabled>No tasks found - Check your database</option>
                  ) : (
                    uniqueTasks.map((task) => (
                      <option key={task.id} value={task.id}>
                        {task.active ? '🟢 ' : '🔴 '}{task.title}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned?</label>
                <select
                  value={filters.photoPackageAssignment}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, photoPackageAssignment: e.target.value as 'all' | 'unassigned' }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="unassigned">Unassigned</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marketplace</label>
                <select
                  value={filters.marketplaceStatus}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, marketplaceStatus: e.target.value as 'all' | 'in' | 'not' }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="in">In Marketplace</option>
                  <option value="not">Not in Marketplace</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters((prev) => ({ ...prev, dateRange: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </select>
              </div>
            </div>
          </div>

          {/* Submissions List */}
          {filteredSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg mb-2">No approved photo submissions found</div>
              <p className="text-gray-400 mb-4">
                {searchTerm || filters.task !== 'all' || filters.category !== 'all' || filters.subcategory !== 'all' || filters.itemType !== 'all' || filters.language !== 'all' || filters.dateRange !== 'all' || filters.photoPackageAssignment !== 'all'
                  ? 'Try adjusting your filters or search terms'
                  : 'Select approved photos to include in a Photo Package. Labels are optional for photo exports.'
                }
              </p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              {/* Select All Header */}
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      filteredSubmissions.length > 0 &&
                      filteredSubmissions.every((s) => selectedSubmissionIds.has(s.id))
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All
                  </span>
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleExportSubmissions}
                    disabled={selectedSubmissionIds.size === 0 || isExportingSubmissions}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      selectedSubmissionIds.size === 0 || isExportingSubmissions
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isExportingSubmissions ? 'Exporting...' : `Export${selectedSubmissionIds.size > 0 ? ` (${selectedSubmissionIds.size} selected)` : ''}`}
                  </button>
                  <button
                    onClick={handleReleaseToMarketplace}
                    disabled={selectedSubmissions.length === 0}
                    className="px-4 py-2 bg-[#00e3ff] text-black rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    title={selectedSubmissions.length === 0 ? 'Select submissions below to release' : 'Release selected photos to the marketplace'}
                  >
                    Release to Marketplace ({selectedSubmissions.length})
                  </button>
                  <button
                    onClick={handleRecallFromMarketplace}
                    disabled={selectedSubmissions.length === 0}
                    className="px-4 py-2 border border-red-600 text-red-700 bg-white rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={selectedSubmissions.length === 0 ? 'Select submissions below to recall' : 'Recall selected photos from the marketplace'}
                  >
                    Recall from Marketplace ({selectedSubmissions.length})
                  </button>
                </div>
              </div>
              <ul className="divide-y divide-gray-200">
                {filteredSubmissions.map((submission) => {
                  const task = tasks[submission.taskId];
                  const isAssigned = !!submission.photoPackageId;
                  const statusLabel = submission.status === 'completed' ? 'Completed' : 'Approved';
                  const dateToShow = submission.approvedAt || submission.completedAt;

                  return (
                    <li key={submission.id} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={selectedSubmissionIds.has(submission.id)}
                              disabled={isAssigned}
                              onChange={(e) => handleSelectSubmission(submission.id, e.target.checked)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              title={isAssigned ? 'Already assigned to a photo package' : 'Select submission'}
                            />
                          </div>
                          <div className="flex-shrink-0">
                            <img
                              src={submission.imageUrl || submission.mediaUrl}
                              alt="Photo submission"
                              className="h-16 w-16 object-cover rounded-lg"
                            />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-medium text-gray-900">
                                {submission.taskTitle || task?.title || 'Untitled'}
                              </div>
                              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                {statusLabel}
                              </span>
                              {isAssigned && (
                                <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                                  Assigned: {submission.photoPackageId}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {submission.category}{submission.subcategory && ` · ${submission.subcategory}`}{submission.itemType && ` · ${submission.itemType}`}{submission.contentLanguageLabel && ` · ${submission.contentLanguageLabel}`}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {submission.id.slice(-8)} • Approved on: {formatDate(dateToShow)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {(submission as any).marketplace?.rawPhoto ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              In Marketplace
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Not in Marketplace
                            </span>
                          )}
                          <button
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setShowDetailsModal(true);
                            }}
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Packages Tab */}
      {activeTab === 'packages' && (
        <div className="space-y-6">
          {/* Packages Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-purple-600">{packages.length}</div>
              <div className="text-sm text-gray-600">Total Packages</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-blue-600">
                {packages.reduce((sum, pkg) => sum + (pkg.stats?.images || 0), 0)}
              </div>
              <div className="text-sm text-gray-600">Total Images</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-green-600">
                {[...new Set(submissions.filter(s => s.photoPackageId && packages.some(pkg => pkg.id === s.photoPackageId)).map(s => s.subcategory).filter(Boolean))].length}
              </div>
              <div className="text-sm text-gray-600">Total SubCategories</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-orange-600">
                {packages.filter((pkg) => pkg.isPublished).length}
              </div>
              <div className="text-sm text-gray-600">Published</div>
            </div>
          </div>

          {/* Photo Packages List */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Photo Packages</h3>
                <p className="text-sm text-gray-500">Packages published to the Client Portal marketplace.</p>
              </div>
              {packages.length > 0 && (
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={packages.length > 0 && packages.every((pkg) => selectedPackageIds.has(pkg.id))}
                    onChange={(e) => handleSelectAllPackages(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Select All</span>
                </label>
              )}
            </div>
            {packages.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📸</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Photo Packages Yet</h3>
                <p className="text-gray-500 mb-4">
                  Go to the Submissions tab to select photo submissions and create your first photo package.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                        <input
                          type="checkbox"
                          checked={packages.length > 0 && packages.every((pkg) => selectedPackageIds.has(pkg.id))}
                          onChange={(e) => handleSelectAllPackages(e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Images</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categories</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Published</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Language
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {packages.map((pkg) => (
                      <tr key={pkg.id} className={selectedPackageIds.has(pkg.id) ? 'bg-blue-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedPackageIds.has(pkg.id)}
                            onChange={(e) => handlePackageSelect(pkg.id, e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{pkg.name}</div>
                          <div className="text-sm text-gray-500">{pkg.description || 'No description'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">{pkg.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {pkg.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pkg.stats?.images || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pkg.stats?.categories?.length || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              pkg.isPublished ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {pkg.isPublished ? 'Published' : 'Unpublished'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {pkg.primaryLanguageCode
                            ? pkg.languageMix === 'mixed'
                              ? 'Mixed'
                              : (pkg.languageLabels?.[0] || pkg.primaryLanguageCode)
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button onClick={() => handleEditPackage(pkg)} className="text-blue-600 hover:text-blue-900">
                              Edit
                            </button>
                            <button onClick={() => togglePublishPackage(pkg)} className="text-indigo-600 hover:text-indigo-900">
                              {pkg.isPublished ? 'Unpublish' : 'Publish'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Package Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-xl shadow-lg rounded-md bg-white">
            <div className="mt-3 space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Create Photo Package</h3>
              <p className="text-sm text-gray-600">You selected {selectedSubmissions.length} photo submissions.</p>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={packageForm.name}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Kitchen Handwriting Photos – Batch 1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package ID (slug)</label>
                  <input
                    type="text"
                    value={packageForm.packageId}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, packageId: slugify(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="kitchen-handwriting-batch-1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={packageForm.description}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Short summary of what this photo package contains."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (USD)</label>
                    <input
                      type="number"
                      min="0"
                      value={packageForm.priceUsd}
                      onChange={(e) => setPackageForm((prev) => ({ ...prev, priceUsd: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                    <input
                      type="text"
                      value={packageForm.version}
                      onChange={(e) => setPackageForm((prev) => ({ ...prev, version: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="1"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                  <input
                    type="text"
                    value={packageForm.tags}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="kitchen, handwriting, photos"
                  />
                </div>
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={packageForm.publishNow}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, publishNow: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Publish immediately</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200">
                  Cancel
                </button>
                <button
                  onClick={handleCreatePackage}
                  disabled={creatingPackage}
                  className="px-4 py-2 text-sm text-white rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {creatingPackage ? 'Creating...' : 'Create Photo Package'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Package Modal */}
      {showEditModal && editingPackage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-xl shadow-lg rounded-md bg-white">
            <div className="mt-3 space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Photo Package</h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={packageForm.name}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={packageForm.description}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (USD)</label>
                    <input
                      type="number"
                      min="0"
                      value={packageForm.priceUsd}
                      onChange={(e) => setPackageForm((prev) => ({ ...prev, priceUsd: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                    <input
                      type="text"
                      value={packageForm.version}
                      onChange={(e) => setPackageForm((prev) => ({ ...prev, version: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <input
                    type="text"
                    value={packageForm.tags}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={packageForm.publishNow}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, publishNow: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Published</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingPackage(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button onClick={handleUpdatePackage} className="px-4 py-2 text-sm text-white rounded bg-blue-600 hover:bg-blue-700">
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedSubmission && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Photo Submission Details - {selectedSubmission.taskTitle || tasks[selectedSubmission.taskId]?.title || 'Untitled'}
                </h3>
                  {isMultiPhotoSubmission(selectedSubmission) && (
                    <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                      📷 Multi-Photo
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedSubmission(null);
                    setActiveRoleDetails('primary');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Image */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Photo</h4>
                  
                  {/* Multi-photo Role Toggle */}
                  {isMultiPhotoSubmission(selectedSubmission) && (
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-purple-700">View:</span>
                        <div className="flex gap-1">
                          {getRequiredRoles(selectedSubmission).map((role) => (
                            <button
                              key={role}
                              onClick={() => setActiveRoleDetails(role)}
                              className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                activeRoleDetails === role
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                              }`}
                            >
                              {getRoleDisplayLabel(role, selectedSubmission)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="relative border border-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src={getImageUrlForRole(selectedSubmission, activeRoleDetails) || ''} 
                      alt={`Photo submission - ${getRoleDisplayLabel(activeRoleDetails, selectedSubmission)}`}
                      className="w-full h-auto max-h-96 object-contain"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/placeholder.png';
                      }}
                    />
                    {isMultiPhotoSubmission(selectedSubmission) && (
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black bg-opacity-60 text-white text-xs rounded">
                        {getRoleDisplayLabel(activeRoleDetails, selectedSubmission)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Submission Details */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Submission Information</h4>
                  
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Submission ID:</span>
                      <span className="text-gray-900 font-mono text-sm">{selectedSubmission.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Task:</span>
                      <span className="text-gray-900">{tasks[selectedSubmission.taskId]?.title || selectedSubmission.taskTitle || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Category:</span>
                      <span className="text-gray-900">{selectedSubmission.category || 'None'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Subcategory:</span>
                      <span className="text-gray-900">{selectedSubmission.subcategory || tasks[selectedSubmission.taskId]?.subcategory || 'None'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Item Type:</span>
                      <span className="text-gray-900">{selectedSubmission.itemType || tasks[selectedSubmission.taskId]?.itemType || 'None'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Task Type:</span>
                      <span className="text-gray-900">{selectedSubmission.taskType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Status:</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {selectedSubmission.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Approved on:</span>
                      <span className="text-gray-900">{formatDate(selectedSubmission.approvedAt || selectedSubmission.completedAt)}</span>
                    </div>
                    {selectedSubmission.photoPackageId && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Photo Package ID:</span>
                        <span className="text-gray-900 font-mono text-sm">{selectedSubmission.photoPackageId}</span>
                      </div>
                    )}
                    {selectedSubmission.labeledBy && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Labeled By:</span>
                        <span className="text-gray-900">{selectedSubmission.labeledBy}</span>
                      </div>
                    )}
                    {selectedSubmission.approvedBy && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Approved By:</span>
                        <span className="text-gray-900">{selectedSubmission.approvedBy}</span>
                      </div>
                    )}
                  </div>

                  {/* Labels/Objects Details */}
                  {(() => {
                    // For multi-photo, use role-specific labels
                    const labelsToUse = isMultiPhotoSubmission(selectedSubmission)
                      ? getActiveLabels(selectedSubmission, activeRoleDetails)
                      : selectedSubmission.labels;
                    const parsedLabels = parseLabels(labelsToUse);
                    
                    // Show label counts summary for multi-photo
                    if (isMultiPhotoSubmission(selectedSubmission)) {
                      const requiredRoles = getRequiredRoles(selectedSubmission);
                      const labelCounts = requiredRoles.map(role => {
                        const roleLabels = getActiveLabels(selectedSubmission, role);
                        return `${role === 'primary' ? 'Pri' : (role === 'expiration_label' ? 'Exp' : 'After')}: ${roleLabels.length}`;
                      }).join(', ');
                      
                      return (
                        <div className="space-y-3 mt-4">
                          <div className="flex items-center justify-between">
                            <h5 className="text-sm font-medium text-gray-900">
                              Labels & Objects ({parsedLabels.length})
                              <span className="text-xs text-gray-500 ml-2">
                                - {getRoleDisplayLabel(activeRoleDetails, selectedSubmission)}
                              </span>
                            </h5>
                            <span className="text-xs text-purple-600 font-medium">
                              {labelCounts}
                            </span>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                            {parsedLabels.length === 0 ? (
                              <div className="text-sm text-gray-500 text-center py-4">
                                No labels for {getRoleDisplayLabel(activeRoleDetails, selectedSubmission)}
                              </div>
                            ) : (
                              parsedLabels.map((label, index) => (
                                <div key={index} className="mb-2 p-2 bg-white rounded border">
                                  <div className="text-sm">
                                    <span className="font-medium">Object Name:</span> <span className="text-blue-600">{label.objectName}</span>
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    <span className="font-medium">Object ID:</span> <span className="font-mono">{label.objectId}</span>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    <span className="font-medium">Description:</span> {label.objectDescription}
                                  </div>
                                  {label.bboxInfo && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {label.bboxInfo}
                                    </div>
                                  )}
                                  {Object.keys(label.attributes).length > 0 && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      Attributes: {JSON.stringify(label.attributes)}
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    }
                    
                    // Single-photo: original behavior
                    if (parsedLabels.length > 0) {
                      return (
                        <div className="space-y-3 mt-4">
                          <h5 className="text-sm font-medium text-gray-900">Labels & Objects ({parsedLabels.length})</h5>
                          <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                            {parsedLabels.map((label, index) => (
                              <div key={index} className="mb-2 p-2 bg-white rounded border">
                                <div className="text-sm">
                                  <span className="font-medium">Object Name:</span> <span className="text-blue-600">{label.objectName}</span>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  <span className="font-medium">Object ID:</span> <span className="font-mono">{label.objectId}</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  <span className="font-medium">Description:</span> {label.objectDescription}
                                </div>
                                {label.bboxInfo && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {label.bboxInfo}
                                  </div>
                                )}
                                {Object.keys(label.attributes).length > 0 && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Attributes: {JSON.stringify(label.attributes)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedSubmission(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

